import {
  AnswerExpression,
  AstValue,
  BoolOperator,
  BooleanExpression,
  CompareExpression,
  CompareOperator,
  Expression,
  ExpressionType,
  FormatExpression,
  FormatType,
  Key,
  ListExpression,
  ListOperator,
  LiteralExpression,
  MathExpression,
  MathOperator,
} from '@bybeam/platform-types';

export function answer(key: Key): AnswerExpression {
  return { type: ExpressionType.Answer, key };
}

// Bool Expressions
export function and(expressions: Expression[]): BooleanExpression {
  return {
    type: ExpressionType.Boolean,
    op: BoolOperator.And,
    expressions,
  };
}

export function or(expressions: Expression[]): BooleanExpression {
  return {
    type: ExpressionType.Boolean,
    op: BoolOperator.Or,
    expressions,
  };
}

export function not(expression: Expression): BooleanExpression {
  return {
    type: ExpressionType.Boolean,
    op: BoolOperator.Not,
    expression,
  };
}

// Compare expressions
export function eq(key: Key, value: AstValue): CompareExpression {
  return {
    type: ExpressionType.Compare,
    op: CompareOperator.Eq,
    comparators: [answer(key), literal(value)],
  };
}

export function gt(key: Key, min: number): CompareExpression {
  return {
    type: ExpressionType.Compare,
    op: CompareOperator.Gt,
    comparators: [answer(key), literal(min)],
  };
}

export function gte(key: Key, min: number): CompareExpression {
  return {
    type: ExpressionType.Compare,
    op: CompareOperator.Gte,
    comparators: [answer(key), literal(min)],
  };
}

export function lte(key: Key, max: number): CompareExpression {
  return {
    type: ExpressionType.Compare,
    op: CompareOperator.Lte,
    comparators: [answer(key), literal(max)],
  };
}

export function inList(key: Key, value: AstValue): CompareExpression {
  return {
    type: ExpressionType.Compare,
    op: CompareOperator.In,
    comparators: [answer(key), literal(value)],
  };
}

// Literal
export function literal(value: AstValue | AstValue[]): LiteralExpression {
  return { type: ExpressionType.Literal, value };
}

// Format expressions
export function format(format: Exclude<FormatType, 'pattern'>, key: Key): FormatExpression {
  return {
    type: ExpressionType.Format,
    format,
    value: answer(key),
  };
}

export function pattern(pattern: string, key: Key): FormatExpression {
  return { type: ExpressionType.Format, format: FormatType.Pattern, pattern, value: answer(key) };
}

// List expressions
export function empty(key: Key): CompareExpression {
  return {
    type: ExpressionType.Compare,
    op: CompareOperator.Eq,
    comparators: [
      {
        type: ExpressionType.List,
        op: ListOperator.Length,
        list: answer(key),
      },
      literal(0),
    ],
  };
}

export function sum(listKey: Key, objKey?: Key): ListExpression {
  return {
    type: ExpressionType.List,
    op: ListOperator.Sum,
    list: objKey
      ? {
          type: ExpressionType.List,
          op: ListOperator.Map,
          list: answer(listKey),
          mapper: answer(objKey),
        }
      : answer(listKey),
  };
}

// Math expressions
export function add(key: Key, value: number): MathExpression {
  return {
    type: ExpressionType.Math,
    op: MathOperator.Add,
    expressions: [answer(key), literal(value)],
  };
}

export function multiply(key: Key, value: number): MathExpression {
  return {
    type: ExpressionType.Math,
    op: MathOperator.Multiply,
    expressions: [answer(key), literal(value)],
  };
}
